'use client';

import Link, { type LinkProps } from 'next/link';
import { clsx } from 'clsx';

type ButtonLinkProps = LinkProps &
  React.AnchorHTMLAttributes<HTMLAnchorElement> & {
    variant?: 'primary' | 'outline';
  };

const BASE =
  'inline-flex items-center justify-center font-sans text-[0.75rem] font-bold tracking-[0.14em] uppercase transition-colors';

const VARIANTS = {
  primary:
    'bg-primary text-white px-8 py-[0.85rem] hover:bg-color-primary-dark',
  outline:
    'border-[1.5px] border-primary text-primary px-8 py-[0.85rem] hover:bg-primary hover:text-white',
};

export function ButtonLink({
  href,
  variant = 'primary',
  className,
  target,
  rel,
  onClick,
  children,
  ...props
}: ButtonLinkProps) {
  const hrefStr = href.toString();
  const hash = hrefStr.includes('#') ? hrefStr.split('#')[1] : '';
  const isAnchor = hash.length > 0;

  return (
    <Link
      href={href}
      scroll={!isAnchor}
      target={target}
      rel={rel}
      onClick={(e) => {
        if (isAnchor && hash) {
          // If the URL already has this hash, the browser won't scroll again —
          // intercept and scroll manually so repeated clicks always work.
          if (
            typeof window !== 'undefined' &&
            window.location.hash === `#${hash}`
          ) {
            e.preventDefault();
            document
              .getElementById(hash)
              ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }
        onClick?.(e);
      }}
      className={clsx(BASE, VARIANTS[variant], className)}
      {...props}
    >
      {children}
    </Link>
  );
}
